import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeUp } from "./Reveal";

/**
 * ClosingCTA — final CTA section for a page.
 *
 * Variants:
 *  - "dark"  (default): charcoal background, used where the page's own
 *                       final content already provides a warm section
 *                       above (Home, About, Executive Guide, etc.).
 *  - "light" : warm off-white background, used on pages whose previous
 *              section is dark so the CTA does not visually merge into
 *              the footer (Operational Clarity, How BOS Works,
 *              Operational Friction Review).
 */
export const ClosingCTA = ({
  title = "Start with one area of work that keeps coming back.",
  copy = "The Operational Clarity Review takes about five minutes and gives you a complete result on screen.",
  showDiscussLink = true,
  variant = "dark",
}) => {
  const isLight = variant === "light";
  return (
    <section
      data-testid="closing-cta"
      data-variant={variant}
      className={
        isLight
          ? "bg-paper text-ink border-t border-rule"
          : "bg-ink text-paper"
      }
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <FadeUp>
          <h2 className="mt-0 max-w-3xl font-display text-3xl md:text-5xl tracking-tight leading-tight">
            {title}
          </h2>
          <p
            className={`mt-6 max-w-xl text-base leading-relaxed ${
              isLight ? "text-mute" : "text-paper/60"
            }`}
          >
            {copy}
          </p>
          <div className="mt-10">
            <Link
              to="/operational-clarity-review"
              data-testid="cta-take-review"
              className={`inline-flex items-center gap-2 bg-terra text-white text-[12px] font-semibold uppercase tracking-[0.12em] px-6 py-3.5 transition-colors duration-300 ${
                isLight
                  ? "hover:bg-ink hover:text-paper"
                  : "hover:bg-paper hover:text-ink"
              }`}
            >
              Start the Review <ArrowUpRight size={15} />
            </Link>
          </div>
          {showDiscussLink && (
            <div className="mt-6">
              <Link
                to="/contact"
                data-testid="cta-discuss-friction"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-terra link-rule"
              >
                Talk through the problem <ArrowUpRight size={13} />
              </Link>
            </div>
          )}
        </FadeUp>
      </div>
    </section>
  );
};
